const SOURCE_LABELS = {
  ai_price_import: 'Invoice import',
  email_invoice_import: 'Emailed invoice',
  invoice_approval: 'Invoice approved',
  import_approval: 'Import approved',
  item_editor: 'Item edited',
  web_count: 'Count updated',
  mobile_count: 'Count updated',
  mobile_count_close: 'Count updated',
};

const asNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(number) ? number : null;
};

const validTimestamp = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
};

const readableSource = (value) => {
  if (!value) return 'Catalog';
  if (SOURCE_LABELS[value]) return SOURCE_LABELS[value];
  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const priceFor = (row, basis, side) => asNumber(row?.[`${side}_${basis}_price`]);

const hasRecordedMovement = (row, basis) => {
  const oldPrice = priceFor(row, basis, 'old');
  const newPrice = priceFor(row, basis, 'new');
  if (oldPrice === null || oldPrice <= 0) return false;
  if (row?.change_type === 'removed' && newPrice === null) return true;
  return newPrice !== null && oldPrice !== newPrice;
};

/**
 * Pick the business price represented by an item's history. An actual recorded
 * movement wins over a current catalog value, so a unit-price change cannot be
 * mislabeled as case history merely because case_price is also populated.
 */
export function chooseItemPriceBasis(item, rows = [], preferredBasis = null) {
  if (preferredBasis === 'case' || preferredBasis === 'unit') return preferredBasis;
  if (rows.some((row) => hasRecordedMovement(row, 'case'))) return 'case';
  if (rows.some((row) => hasRecordedMovement(row, 'unit'))) return 'unit';
  const hasCasePrice = (asNumber(item?.case_price) ?? 0) > 0 || rows.some((row) =>
    (priceFor(row, 'case', 'old') ?? 0) > 0 || (priceFor(row, 'case', 'new') ?? 0) > 0);
  return hasCasePrice ? 'case' : 'unit';
}

const sameMoment = (left, right) => left !== null && right !== null && Math.abs(left - right) <= 5 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

export const formatTimelineMoney = (value) => {
  const number = asNumber(value);
  return number === null ? 'Not priced' : `$${number.toFixed(2)}`;
};

/**
 * Build a true time scale for the spark timeline. Unlike formatted date
 * categories, millisecond values remain unique when several events occur on
 * the same calendar day. The visible domain belongs to this item only: oldest
 * event through newest event.
 */
export function buildTimelineChartScale(events = [], maxTicks = 5) {
  const data = (Array.isArray(events) ? events : []).map((event, index) => ({
    ...event,
    index,
    eventTime: validTimestamp(event?.timestamp),
  })).filter((event) => event.eventTime !== null);

  if (!data.length) return { data: [], domain: [0, 1], ticks: [], spanMs: 0 };
  const oldest = Math.min(...data.map((event) => event.eventTime));
  const newest = Math.max(...data.map((event) => event.eventTime));
  const rawSpan = newest - oldest;
  const byMoment = new Map();
  data.forEach((event) => {
    if (!byMoment.has(event.eventTime)) byMoment.set(event.eventTime, []);
    byMoment.get(event.eventTime).push(event);
  });

  // A zero-width time domain cannot render distinct points. Keep the real
  // event as the tick while spreading same-moment events inside a one-day
  // display window. Their business timestamps remain unchanged on `timestamp`.
  if (rawSpan === 0) {
    const group = byMoment.get(oldest);
    const spread = DAY_MS * 0.6;
    group.forEach((event, index) => {
      event.time = group.length === 1
        ? oldest
        : oldest - (spread / 2) + ((spread * index) / (group.length - 1));
    });
    return {
      data,
      domain: [oldest - (DAY_MS / 2), newest + (DAY_MS / 2)],
      ticks: [oldest],
      spanMs: 0,
    };
  }

  // When two events share an exact timestamp, give each a small display-only
  // offset within this item's real oldest/newest domain. This preserves the
  // requested date range while keeping every plot point visible.
  const slot = Math.min(DAY_MS / 8, rawSpan / (Math.max(data.length, 2) * 4));
  byMoment.forEach((group, moment) => {
    if (group.length === 1) {
      group[0].time = moment;
      return;
    }
    group.forEach((event, index) => {
      if (moment === oldest) event.time = moment + (slot * index);
      else if (moment === newest) event.time = moment - (slot * (group.length - 1 - index));
      else event.time = moment + (slot * (index - ((group.length - 1) / 2)));
    });
  });

  const tickCount = Math.max(2, Math.min(maxTicks, data.length + 2));
  const ticks = Array.from({ length: tickCount }, (_, index) =>
    oldest + ((rawSpan * index) / (tickCount - 1)));
  return { data, domain: [oldest, newest], ticks, spanMs: rawSpan };
}

export function formatTimelineAxisTick(value, spanMs) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  if (spanMs < 2 * DAY_MS) {
    return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }
  if (spanMs > 365 * DAY_MS) {
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Convert one item's durable price_history rows into business-facing events.
 * The first event is always the catalog addition; later events retain their
 * exact stored dates so this data can also drive reports and other spark views.
 *
 * `options.multiVendor` suppresses the item-level vendor fallback: on an item
 * bought from several vendors, a price change with no recorded invoice vendor
 * is genuinely unattributed, and borrowing the item's legacy vendor field
 * would credit it to a supplier that may not have made the change.
 */
export function buildItemPriceTimeline(item, historyRows = [], preferredBasis = null, options = {}) {
  const multiVendor = options?.multiVendor === true;
  const rows = (Array.isArray(historyRows) ? historyRows : [])
    .filter((row) => validTimestamp(row?.changed_at) !== null)
    .slice()
    .sort((a, b) => validTimestamp(a.changed_at) - validTimestamp(b.changed_at));
  const basis = chooseItemPriceBasis(item || {}, rows, preferredBasis);
  const firstRow = rows[0] || null;
  const setupRow = rows.find((row) => row.change_type === 'setup') || null;
  const itemCreatedAt = validTimestamp(item?.created_at);
  const addedAt = itemCreatedAt ?? validTimestamp(setupRow?.changed_at) ?? validTimestamp(firstRow?.changed_at) ?? Date.now();

  let addedPrice = null;
  if (setupRow && (itemCreatedAt === null || sameMoment(itemCreatedAt, validTimestamp(setupRow.changed_at)))) {
    addedPrice = priceFor(setupRow, basis, 'new');
  } else if (firstRow) {
    const firstOld = priceFor(firstRow, basis, 'old');
    const firstNew = priceFor(firstRow, basis, 'new');
    if (firstOld !== null && firstOld > 0) addedPrice = firstOld;
    else if (sameMoment(itemCreatedAt, validTimestamp(firstRow.changed_at))) addedPrice = firstNew;
  } else {
    addedPrice = asNumber(item?.[`${basis}_price`]);
  }

  const events = [{
    id: `item-added-${item?.id ?? 'item'}`,
    type: 'added',
    timestamp: new Date(addedAt).toISOString(),
    title: 'Item added',
    description: addedPrice === null ? 'Added to the item catalog' : `Added at ${formatTimelineMoney(addedPrice)}`,
    price: addedPrice,
    previousPrice: null,
    source: 'Catalog',
    vendor: multiVendor ? '' : (item?.vendor || ''),
  }];

  rows.forEach((row) => {
    const rowTime = validTimestamp(row.changed_at);
    const oldPrice = priceFor(row, basis, 'old');
    const rawNewPrice = priceFor(row, basis, 'new');
    const newPrice = row.change_type === 'removed' && rawNewPrice === null ? 0 : rawNewPrice;

    // A setup row represents the same moment and price as the synthetic catalog
    // addition, so show one clear event instead of two overlapping points.
    const duplicatesAddedEvent = sameMoment(addedAt, rowTime)
      && oldPrice === null
      && newPrice === addedPrice;
    if ((row === setupRow && sameMoment(addedAt, rowTime)) || duplicatesAddedEvent) return;
    if (oldPrice === null && newPrice === null) return;
    if (oldPrice !== null && newPrice !== null && oldPrice === newPrice) return;

    let title = 'Price updated';
    if (row.change_type === 'removed' || newPrice === 0) title = 'Price removed';
    else if (oldPrice === null || oldPrice === 0) title = 'Price set';
    else if (newPrice !== null && newPrice > oldPrice) title = 'Price increased';
    else if (newPrice !== null && newPrice < oldPrice) title = 'Price decreased';

    let description;
    if (title === 'Price removed') description = `${formatTimelineMoney(oldPrice)} → Not priced`;
    else if (oldPrice === null || oldPrice === 0) description = `Set to ${formatTimelineMoney(newPrice)}`;
    else description = `${formatTimelineMoney(oldPrice)} → ${formatTimelineMoney(newPrice)}`;

    events.push({
      id: row.id || `price-${row.changed_at}-${events.length}`,
      type: 'price_update',
      timestamp: new Date(rowTime).toISOString(),
      title,
      description,
      price: newPrice,
      previousPrice: oldPrice,
      source: readableSource(row.source),
      // Attribute the change to the vendor the price actually came from. The
      // item's own legacy vendor field is a last resort, and a deliberate
      // non-answer once the item has several vendor products — naming one of
      // them would credit a price change to a supplier that did not make it.
      vendor: row.invoice_vendor
        || (multiVendor ? '' : (item?.vendor || '')),
      purchaseOptionId: row.purchase_option_id ?? null,
    });
  });

  return { basis, events };
}
